import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonCard,
  IonModal,
  IonToast,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonActionSheet,
  IonAlert,
  IonSearchbar,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonLabel,
  IonCardContent,
} from '@ionic/react';
import {
  addOutline,
  calendarOutline,
  locationOutline,
  ellipsisVertical,
  trashOutline,
  ticketOutline,
  cartOutline,
  createOutline,
  peopleOutline,
  closeOutline,
  checkmarkCircle,
  eyeOutline,
  refreshOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import api, { eventService, ticketService, saleService } from '../services/api';
import { Event } from '../types';
import { format } from 'date-fns';
import CreateEventForm from '../components/CreateEventForm';
import { useEvent } from '../contexts/EventContext';
import { premierLeagueService, Match, Team } from '../services/premierLeague';
import { createMatchName, cleanTeamName } from '../utils/teamUtils';
import { getStadiumForTeam, getCountryForStadium } from '../utils/stadiumMappings';
import CenteredLoader from '../components/CenteredLoader';
import AddTicketModal from '../components/AddTicketModal';
import AddSaleModal from '../components/AddSaleModal';
import EnhancedEventSelector from '../components/EnhancedEventSelector';
import { eventBus, EVENTS } from '../utils/eventBus';
import {
  IonItemDivider,
  IonCardSubtitle,
  IonNote,
  IonSkeletonText,
  IonProgressBar,
} from '@ionic/react';
import {
  chevronDownOutline,
  chevronForwardOutline,
  footballOutline,
  searchOutline,
  sparklesOutline,
} from 'ionicons/icons';

const Events: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { refreshEvents: refreshEventContext } = useEvent();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);

  // Modal states for quick actions
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [showAddSaleModal, setShowAddSaleModal] = useState(false);
  const [eventForTicket, setEventForTicket] = useState<Event | null>(null);
  const [eventForSale, setEventForSale] = useState<Event | null>(null);
  const [pendingMatch, setPendingMatch] = useState<Match | null>(null);

  // Premier League search states
  const [teams, setTeams] = useState<Team[]>([]);
  const [otherMatches, setOtherMatches] = useState<Match[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showMyEvents, setShowMyEvents] = useState(true);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showOtherEvents, setShowOtherEvents] = useState(true);
  const [searchingTeam, setSearchingTeam] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState('');

  // Real-time inventory and sales data

  const [eventStats, setEventStats] = useState<{
    [eventId: string]: {
      inventory: number;
      assignedInventory: number;
      inventoryLeft: number;
      sold: number;
    };
  }>({});

  useEffect(() => {
    loadEvents();
    loadTeams();
  }, []);

  const loadEvents = async () => {
    try {
      setError(null);
      setLoading(true);
      const data = await eventService.getAllEvents();
      setEvents(data || []);

      // Load real-time stats for each event
      if (data && data.length > 0) {
        await loadEventStats(data);
      }
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const loadEventStats = async (eventsList?: Event[]) => {
    const eventsToLoad = eventsList || events;

    if (eventsToLoad.length === 0) {
      setEventStats({});
      return;
    }

    const eventIds = eventsToLoad.map((e) => e._id);

    try {
      // Make both requests in parallel for better performance
      const [inventoryStats, salesStats] = await Promise.all([
        ticketService.getBulkStats(eventIds),
        saleService.getBulkStats(eventIds),
      ]);

      // Combine the results
      const stats: {
        [eventId: string]: {
          inventory: number;
          assignedInventory: number;
          inventoryLeft: number;
          sold: number;
        };
      } = {};
      eventIds.forEach((eventId) => {
        stats[eventId] = {
          inventory: inventoryStats[eventId]?.inventory || 0,
          assignedInventory: inventoryStats[eventId]?.assignedInventory || 0,
          inventoryLeft: inventoryStats[eventId]?.inventoryLeft || 0,
          sold: salesStats[eventId]?.sold || 0,
        };
      });

      setEventStats(stats);
    } catch (error) {
      console.error('Failed to load event stats:', error);
      // Set all stats to 0 in case of error
      const stats: {
        [eventId: string]: {
          inventory: number;
          assignedInventory: number;
          inventoryLeft: number;
          sold: number;
        };
      } = {};
      eventIds.forEach((eventId) => {
        stats[eventId] = {
          inventory: 0,
          assignedInventory: 0,
          inventoryLeft: 0,
          sold: 0,
        };
      });
      setEventStats(stats);
    }
  };

  const loadTeams = async () => {
    try {
      // Load all major league teams for team search
      const allTeams = await premierLeagueService.getAllMajorLeagueTeams();
      setTeams(allTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      // Fallback to English teams only if all teams fail
      try {
        const [premierTeams, championshipTeams] = await Promise.all([
          premierLeagueService.getTeams(),
          premierLeagueService.getEFLChampionshipTeams(),
        ]);
        const englishTeams = [...premierTeams, ...championshipTeams];
        setTeams(englishTeams);
      } catch (fallbackError) {
        console.error('Error loading fallback teams:', fallbackError);
      }
    }
  };

  const searchTeamMatches = async (teamName: string) => {
    if (!teamName.trim()) {
      setOtherMatches([]);
      setSearchingTeam(false);
      return;
    }

    setLoadingMatches(true);
    setSearchingTeam(true);

    try {
      const searchLower = teamName.toLowerCase();

      // Check if we have a matching team in our loaded teams
      const matchingTeam = teams.find(
        (team) =>
          team.name.toLowerCase().includes(searchLower) ||
          team.shortName.toLowerCase().includes(searchLower)
      );

      let teamMatches: Match[] = [];

      if (matchingTeam) {
        // Use team-specific API which returns all matches including cups
        teamMatches = await premierLeagueService.getTeamMatches(
          matchingTeam.id
        );
      } else {
        // Fallback: Use all-competitions search for teams not in our list
        const allMatches = await premierLeagueService.getAllMajorMatches();

        // Filter matches that include the search term
        teamMatches = allMatches.filter(
          (match) =>
            match.homeTeam.name.toLowerCase().includes(searchLower) ||
            match.homeTeam.shortName.toLowerCase().includes(searchLower) ||
            match.awayTeam.name.toLowerCase().includes(searchLower) ||
            match.awayTeam.shortName.toLowerCase().includes(searchLower)
        );
      }

      // Get event names from my events to filter out existing ones
      const myEventNames = events.map((event) => event.name.toLowerCase());

      // Filter out matches that are already in my events
      const newMatches = teamMatches.filter((match) => {
        const matchName = createMatchName(
          match.homeTeam.name,
          match.awayTeam.name
        ).toLowerCase();
        return !myEventNames.includes(matchName);
      });

      // Show all matches (no date filter)
      setOtherMatches(newMatches);
    } catch (error) {
      console.error('Error searching team matches:', error);
      setOtherMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleAddMatchAsEvent = async (match: Match): Promise<Event | null> => {
    try {
      const matchDate = new Date(match.utcDate);
      const stadium = match.venue || getStadiumForTeam(match.homeTeam.name);
      const country = getCountryForStadium(stadium);
      
      const eventData: Partial<Event> = {
        name: createMatchName(match.homeTeam.name, match.awayTeam.name),
        date: matchDate,
        time: matchDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        venue: stadium,
        city: '', // Leave city empty for now - could be enhanced later
        country: country,
        description: `${match.competition.name} - Matchday ${match.matchday}`,
        imageUrl: match.homeTeam.crest,
        status: 'active' as const,
      };

      const createdEvent = await eventService.createEvent(eventData);

      // Don't show toast here - it will be shown after the sale/ticket is created
      // Don't reload events here - it will be done after the modal completes

      // Just remove the match from other matches
      setOtherMatches((prev) => prev.filter((m) => m.id !== match.id));

      return createdEvent;
    } catch (error: any) {
      setToastMessage(error.message || t('errors.generic'));
      setShowToast(true);
      return null;
    }
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadEvents();
    event.detail.complete();
  };

  const handleManualRefresh = async () => {
    await loadEventStats();
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    try {
      await eventService.deleteEvent(eventToDelete._id);
      setToastMessage(t('events.deleteEventSuccess'));
      setShowToast(true);
      setShowDeleteAlert(false);
      setEventToDelete(null);
      await loadEvents();
      await refreshEventContext(); // Refresh the event context
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const handleQuickAction = (event: Event, action: string) => {
    setSelectedEvent(event);
    setShowActionSheet(false);

    switch (action) {
      case 'edit':
        setShowEditModal(true);
        break;
      case 'delete':
        setEventToDelete(event);
        setShowDeleteAlert(true);
        break;
      case 'addTicket':
        // Open the add ticket modal
        setEventForTicket(event);
        setShowAddTicketModal(true);
        break;
      case 'addSale':
        // Open the add sale modal
        setEventForSale(event);
        setShowAddSaleModal(true);
        break;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'past':
        return 'medium';
      case 'cancelled':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getEventStatus = (event: Event): string => {
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (event.status === 'cancelled') return 'cancelled';
    if (eventDate < today) return 'past';
    return 'active';
  };

  // Filter my events based on search (if it's a team name)
  const filteredMyEvents = searchingTeam
    ? events.filter((event) => {
        const searchLower = searchText.toLowerCase();
        return event.name.toLowerCase().includes(searchLower);
      })
    : events;

  // Separate active and past events
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const activeEvents = filteredMyEvents.filter((event) => {
    const eventDate = new Date(event.date);
    return eventDate >= today && event.status !== 'cancelled';
  });

  const pastEvents = filteredMyEvents.filter((event) => {
    const eventDate = new Date(event.date);
    return eventDate < today || event.status === 'cancelled';
  });

  const sortedActiveEvents = [...activeEvents].sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

  const sortedPastEvents = [...pastEvents].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime(); // Most recent first
  });

  // Debounce search text
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  // Perform search when debounced text changes
  useEffect(() => {
    searchTeamMatches(debouncedSearchText);
  }, [debouncedSearchText, teams, events]);

  // Listen for fulfillment changes to refresh event stats
  useEffect(() => {
    const handleRefreshEvents = async () => {
      if (events.length > 0) {
        await loadEventStats(events);
      }
    };

    // Listen for fulfillment updates - store unsubscribe functions
    const unsubscribeTickets = eventBus.on(
      EVENTS.REFRESH_TICKETS,
      handleRefreshEvents
    );
    const unsubscribeSales = eventBus.on(
      EVENTS.REFRESH_SALES,
      handleRefreshEvents
    );

    // Also refresh when the page becomes visible (e.g., navigating back)
    const handleVisibilityChange = () => {
      if (!document.hidden && events.length > 0) {
        loadEventStats(events);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribeTickets();
      unsubscribeSales();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [events]);

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton menu="main-menu" />
            </IonButtons>
            <IonTitle>{t('events.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <CenteredLoader
            message={t('events.loadingEvents') || 'Fetching your events...'}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>{t('events.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleManualRefresh} fill="clear">
              <IonIcon icon={refreshOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value!)}
            placeholder="Search for team across all leagues (e.g., Manchester United, Barcelona)"
            className="px-2"
          />
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonGrid className="p-4">
          {/* Search hint with enhanced UI */}
          {searchText && (
            <div className="mb-4">
              {loadingMatches ? (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg p-3 animate-pulse">
                  <div className="flex items-center justify-center space-x-2">
                    <IonSpinner name="crescent" className="w-4 h-4" />
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      Searching Premier League matches for "{searchText}"...
                    </p>
                  </div>
                  <IonProgressBar
                    type="indeterminate"
                    color="secondary"
                    className="mt-2"
                  />
                </div>
              ) : searchingTeam ? (
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-700">
                  <div className="flex items-center space-x-2">
                    <IonIcon
                      icon={checkmarkCircle}
                      className="text-green-600 dark:text-green-400 text-lg"
                    />
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      Found matches for "{searchText}"
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                  <div className="flex items-center space-x-2">
                    <IonIcon
                      icon={searchOutline}
                      className="text-blue-600 dark:text-blue-400 text-lg"
                    />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Try searching for a team like "Manchester United",
                      "Liverpool", "Arsenal"...
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* My Events Section */}
          <IonCard className="mb-4 shadow-lg">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setShowMyEvents(!showMyEvents)}
            >
              <div className="flex items-center space-x-2">
                <IonIcon
                  icon={
                    showMyEvents ? chevronDownOutline : chevronForwardOutline
                  }
                  className="text-xl"
                />
                <h2 className="text-lg font-semibold">My Events</h2>
                <IonChip color="primary">
                  <IonLabel>{sortedActiveEvents.length}</IonLabel>
                </IonChip>
              </div>
              {searchingTeam && filteredMyEvents.length > 0 && (
                <IonNote color="success">
                  Found {filteredMyEvents.length} matches
                </IonNote>
              )}
            </div>

            {showMyEvents && (
              <IonCardContent className="p-0">
                {sortedActiveEvents.length === 0 ? (
                  <div className="text-center py-8 px-4">
                    <IonIcon
                      icon={calendarOutline}
                      className="text-5xl text-gray-400 mb-2"
                    />
                    <p className="text-gray-500 dark:text-gray-400">
                      {searchingTeam && searchText
                        ? 'No events found for this team'
                        : t('events.noEvents')}
                    </p>
                    {!searchText && (
                      <IonButton
                        className="mt-4"
                        size="small"
                        onClick={() => setShowCreateModal(true)}
                      >
                        <IonIcon icon={addOutline} slot="start" />
                        {t('events.createEvent')}
                      </IonButton>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.eventName')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.date')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.venue')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.ticketsSold')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Assigned Inventory
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Inventory Left
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.status')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedActiveEvents.map((event) => {
                          const status = getEventStatus(event);
                          return (
                            <tr
                              key={event._id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                            >
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                  {event.name}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {format(new Date(event.date), 'MMM dd, yyyy')}
                                </div>
                                {event.time && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {event.time}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {event.venue}
                                </div>
                                {event.city && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {event.city}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.sold || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.assignedInventory ||
                                    0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.inventoryLeft || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <IonChip color={getStatusColor(status)}>
                                  <IonLabel className="text-xs font-medium uppercase px-1 py-0">
                                    {t(`events.status.${status}`)}
                                  </IonLabel>
                                </IonChip>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center space-x-1">
                                  <IonButton
                                    fill="outline"
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      history.push(`/events/${event._id}`)
                                    }
                                    className="text-xs"
                                  >
                                    <IonIcon
                                      icon={eyeOutline}
                                      slot="start"
                                      className="text-sm"
                                    />
                                    {t('events.manage')}
                                  </IonButton>
                                  <IonButton
                                    fill="outline"
                                    size="small"
                                    color="success"
                                    onClick={() =>
                                      handleQuickAction(event, 'addTicket')
                                    }
                                    className="text-xs"
                                  >
                                    <IonIcon
                                      icon={ticketOutline}
                                      slot="start"
                                      className="text-sm"
                                    />
                                    {t('inventory.add')}
                                  </IonButton>
                                  <IonButton
                                    fill="outline"
                                    size="small"
                                    color="warning"
                                    onClick={() =>
                                      handleQuickAction(event, 'addSale')
                                    }
                                    className="text-xs"
                                  >
                                    <IonIcon
                                      icon={cartOutline}
                                      slot="start"
                                      className="text-sm"
                                    />
                                    {t('sales.sell')}
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    onClick={() => {
                                      setSelectedEvent(event);
                                      setShowActionSheet(true);
                                    }}
                                  >
                                    <IonIcon icon={ellipsisVertical} />
                                  </IonButton>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </IonCardContent>
            )}
          </IonCard>

          {/* Past Events Section */}
          {pastEvents.length > 0 && (
            <IonCard className="mb-4 shadow-lg">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                <div className="flex items-center space-x-2">
                  <IonIcon
                    icon={
                      showPastEvents
                        ? chevronDownOutline
                        : chevronForwardOutline
                    }
                    className="text-xl"
                  />
                  <h2 className="text-lg font-semibold">
                    {t('events.pastEvents')}
                  </h2>
                  <IonChip color="medium">
                    <IonLabel>{sortedPastEvents.length}</IonLabel>
                  </IonChip>
                </div>
              </div>

              {showPastEvents && (
                <IonCardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.eventName')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.date')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('events.venue')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('sales.ticketsSold')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Assigned Inventory
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Inventory Left
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.status')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedPastEvents.map((event) => {
                          const status = getEventStatus(event);
                          return (
                            <tr
                              key={event._id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors opacity-75"
                            >
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                  {event.name}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {format(new Date(event.date), 'MMM dd, yyyy')}
                                </div>
                                {event.time && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {event.time}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm text-gray-900 dark:text-gray-100">
                                  {event.venue}
                                </div>
                                {event.city && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {event.city}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.sold || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.assignedInventory ||
                                    0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {eventStats[event._id]?.inventoryLeft || 0}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <IonChip color={getStatusColor(status)}>
                                  <IonLabel className="text-xs font-medium uppercase px-1 py-0">
                                    {t(`events.status.${status}`)}
                                  </IonLabel>
                                </IonChip>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center space-x-1">
                                  <IonButton
                                    fill="outline"
                                    size="small"
                                    color="medium"
                                    onClick={() =>
                                      history.push(`/events/${event._id}`)
                                    }
                                    className="text-xs"
                                  >
                                    <IonIcon
                                      icon={eyeOutline}
                                      slot="start"
                                      className="text-sm"
                                    />
                                    {t('events.viewHistory')}
                                  </IonButton>
                                  <IonButton
                                    fill="clear"
                                    size="small"
                                    onClick={() => {
                                      setSelectedEvent(event);
                                      setShowActionSheet(true);
                                    }}
                                  >
                                    <IonIcon icon={ellipsisVertical} />
                                  </IonButton>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </IonCardContent>
              )}
            </IonCard>
          )}

          {/* Other Events Section - Show when searching or loading */}
          {(searchingTeam && otherMatches.length > 0) ||
          (searchText && loadingMatches) ? (
            <IonCard className="shadow-lg">
              <div
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-200"
                onClick={() => setShowOtherEvents(!showOtherEvents)}
              >
                <div className="flex items-center space-x-2">
                  <IonIcon
                    icon={
                      showOtherEvents
                        ? chevronDownOutline
                        : chevronForwardOutline
                    }
                    className="text-xl transition-transform duration-200"
                  />
                  <h2 className="text-lg font-semibold">Other Matches</h2>
                  {!loadingMatches && (
                    <IonChip color="secondary" className="animate-fade-in">
                      <IonLabel>{otherMatches.length}</IonLabel>
                    </IonChip>
                  )}
                  {otherMatches.length > 0 && (
                    <IonIcon
                      icon={sparklesOutline}
                      className="text-yellow-500 text-sm animate-pulse"
                    />
                  )}
                </div>
                <IonIcon
                  icon={footballOutline}
                  className="text-xl text-purple-500"
                />
              </div>

              {showOtherEvents && (
                <IonCardContent className="p-0">
                  {loadingMatches ? (
                    // Skeleton loading for table
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Match
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Venue
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Competition
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('common.actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {[1, 2, 3].map((i) => (
                            <tr key={i} className="animate-pulse">
                              <td className="px-4 py-3">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-48"></div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-24 mx-auto"></div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-16 mx-auto"></div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Match
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('events.date')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('events.venue')}
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Competition
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              {t('common.actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {otherMatches.map((match) => {
                            const matchDate = new Date(match.utcDate);
                            return (
                              <tr
                                key={match.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                              >
                                <td className="px-4 py-3">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {createMatchName(
                                      match.homeTeam.name,
                                      match.awayTeam.name
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {format(matchDate, 'MMM dd, yyyy')}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {format(matchDate, 'HH:mm')}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {match.venue ||
                                      getStadiumForTeam(match.homeTeam.name)}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {getCountryForStadium(match.venue || getStadiumForTeam(match.homeTeam.name))}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <div className="text-sm text-gray-900 dark:text-gray-100">
                                    {match.competition.name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Matchday {match.matchday}
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <div className="flex items-center justify-center space-x-1">
                                    <IonButton
                                      fill="outline"
                                      size="small"
                                      color="success"
                                      onClick={() => {
                                        // Create a temporary event object for the modal
                                        const tempEvent: Event = {
                                          _id: `temp-${match.id}`,
                                          name: createMatchName(
                                            match.homeTeam.name,
                                            match.awayTeam.name
                                          ),
                                          date: new Date(match.utcDate),
                                          time: new Date(
                                            match.utcDate
                                          ).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false,
                                          }),
                                          venue:
                                            match.venue ||
                                            getStadiumForTeam(
                                              match.homeTeam.name
                                            ),
                                          status: 'active',
                                          userId: '',
                                          totalTickets: 0,
                                          soldTickets: 0,
                                          revenue: 0,
                                        };
                                        setPendingMatch(match);
                                        setEventForTicket(tempEvent);
                                        setShowAddTicketModal(true);
                                      }}
                                      className="text-xs"
                                    >
                                      <IonIcon
                                        icon={ticketOutline}
                                        slot="start"
                                        className="text-sm"
                                      />
                                      {t('inventory.add')}
                                    </IonButton>
                                    <IonButton
                                      fill="outline"
                                      size="small"
                                      color="warning"
                                      onClick={() => {
                                        // Create a temporary event object for the modal
                                        const tempEvent: Event = {
                                          _id: `temp-${match.id}`,
                                          name: createMatchName(
                                            match.homeTeam.name,
                                            match.awayTeam.name
                                          ),
                                          date: new Date(match.utcDate),
                                          time: new Date(
                                            match.utcDate
                                          ).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false,
                                          }),
                                          venue:
                                            match.venue ||
                                            getStadiumForTeam(
                                              match.homeTeam.name
                                            ),
                                          status: 'active',
                                          userId: '',
                                          totalTickets: 0,
                                          soldTickets: 0,
                                          revenue: 0,
                                        };
                                        setPendingMatch(match);
                                        setEventForSale(tempEvent);
                                        setShowAddSaleModal(true);
                                      }}
                                      className="text-xs"
                                    >
                                      <IonIcon
                                        icon={cartOutline}
                                        slot="start"
                                        className="text-sm"
                                      />
                                      {t('sales.sell')}
                                    </IonButton>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </IonCardContent>
              )}
            </IonCard>
          ) : null}

          {/* No matches found state */}
          {searchingTeam &&
            !loadingMatches &&
            otherMatches.length === 0 &&
            searchText && (
              <IonCard className="shadow-lg">
                <IonCardContent className="text-center py-8">
                  <IonIcon
                    icon={footballOutline}
                    className="text-5xl text-gray-400 mb-2"
                  />
                  <p className="text-gray-600 dark:text-gray-400 font-medium">
                    No additional matches found for "{searchText}"
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                    All matches might already be in your events
                  </p>
                </IonCardContent>
              </IonCard>
            )}
        </IonGrid>

        {/* Create Event Modal */}
        <IonModal
          isOpen={showCreateModal}
          onDidDismiss={() => setShowCreateModal(false)}
          className="event-modal-fullscreen"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('events.createEvent')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowCreateModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <CreateEventForm
              onClose={() => setShowCreateModal(false)}
              onSuccess={async () => {
                setShowCreateModal(false);
                await loadEvents();
                await refreshEventContext();
                setToastMessage(t('events.eventCreated'));
                setShowToast(true);
              }}
            />
          </IonContent>
        </IonModal>

        {/* Edit Event Modal */}
        <IonModal
          isOpen={showEditModal}
          onDidDismiss={() => setShowEditModal(false)}
          className="event-modal-fullscreen"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('events.editEvent')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowEditModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedEvent && (
              <CreateEventForm
                event={selectedEvent}
                onClose={() => setShowEditModal(false)}
                onSuccess={async () => {
                  setShowEditModal(false);
                  await loadEvents();
                  await refreshEventContext();
                  setToastMessage(t('events.updateEventSuccess'));
                  setShowToast(true);
                }}
              />
            )}
          </IonContent>
        </IonModal>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: t('common.edit'),
              icon: createOutline,
              handler: () => {
                if (selectedEvent) handleQuickAction(selectedEvent, 'edit');
              },
            },
            {
              text: t('common.delete'),
              icon: trashOutline,
              role: 'destructive',
              handler: () => {
                if (selectedEvent) handleQuickAction(selectedEvent, 'delete');
              },
            },
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
          ]}
        />

        {/* Delete Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header={t('common.confirm')}
          message={t('events.deleteEventConfirm')}
          buttons={[
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
            {
              text: t('common.delete'),
              handler: handleDeleteEvent,
            },
          ]}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />

        {/* Add Ticket Modal */}
        <AddTicketModal
          isOpen={showAddTicketModal}
          onDismiss={() => {
            setShowAddTicketModal(false);
            setEventForTicket(null);
            setPendingMatch(null);
          }}
          event={eventForTicket}
          onBeforeSubmit={async () => {
            if (pendingMatch) {
              const createdEvent = await handleAddMatchAsEvent(pendingMatch);
              if (createdEvent) {
                // Update the event reference for the modal
                setEventForTicket(createdEvent);
              }
              return createdEvent;
            }
            return null;
          }}
          onSuccess={async () => {
            // Close the modal first
            setShowAddTicketModal(false);
            setEventForTicket(null);
            setPendingMatch(null);

            // Show success message
            setToastMessage(t('inventory.addTicketSuccess'));
            setShowToast(true);

            // Refresh events data
            await loadEvents();
            await refreshEventContext();

            // Emit refresh events to update other components
            eventBus.emit(EVENTS.REFRESH_TICKETS);
          }}
        />

        {/* Add Sale Modal */}
        <AddSaleModal
          isOpen={showAddSaleModal}
          onDismiss={() => {
            setShowAddSaleModal(false);
            setEventForSale(null);
            setPendingMatch(null);
          }}
          event={eventForSale}
          onBeforeSubmit={async () => {
            if (pendingMatch) {
              const createdEvent = await handleAddMatchAsEvent(pendingMatch);
              if (createdEvent) {
                // Update the event reference for the modal
                setEventForSale(createdEvent);
              }
              return createdEvent;
            }
            return null;
          }}
          onSuccess={async () => {
            // Close the modal first
            setShowAddSaleModal(false);
            setEventForSale(null);
            setPendingMatch(null);

            // Show success message
            setToastMessage(t('sales.saleRecorded'));
            setShowToast(true);

            // Refresh events data
            await loadEvents();
            await refreshEventContext();

            // Emit refresh events to update other components
            eventBus.emit(EVENTS.REFRESH_SALES);
          }}
        />
      </IonContent>
    </IonPage>
  );
};

export default Events;
